// src/hooks/usePublicUtilitiesLive.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — live WebSocket feed for the Public Utilities page
//
// One place where the page listens to the realtime backend:
//   • utility / asset / sensor / work-order / AI events are subscribed ONCE
//     (handlers read the latest callbacks through refs — no resubscribe churn)
//   • small patches are applied in place immediately (onUtilityPatch / onAssetPatch)
//   • anything that needs a full reload is coalesced into ONE refresh per burst
//     (default 3 s) and paused while the tab is hidden — caught up on return
//   • connection state + a short feed of recent events for the header badge
//
// Event names follow the ones the FlowSense backend already broadcasts elsewhere
// (road_update, work_order_created, anomaly_recorded, asset_prediction_update,
// ai_flood_prediction, utility_status_update …). Unknown names are harmless.
//
// --- UPDATE (v2 — FlowSense linking)
//   • Listens to the cross-page bus (utils/flowsenseBus): a work order or road changed on
//     another page / tab refreshes this page immediately (same coalesced refresh).
//   • Full work-order lifecycle over the socket (updated / assigned / deleted, not only
//     created / completed).
//   • The same change arriving twice (bus + server echo) shows ONE feed entry.
//   • No state updates after unmount.
// --- END UPDATE
//
// --- UPDATE (v3 — Public Utilities ecosystem)
//   • Public Utilities events: asset_lifecycle_update (maintenance / inspection / schedule / status /
//     manager), assets_imported (import + undo) and public_utilities_update (managers, zones,
//     constituency codes, bulk assignment, re-zoning). Status changes patch the row in place first.
//   • asset_status_changed from the server now carries previousStatus — kept on the event.
//   • Socket envelopes { type, payload } and { data } are both unwrapped.
//   • Cross-tab: ASSET_CHANGED on the FlowSense bus (another tab edited an asset) refreshes here;
//     announceAssetChange() lets this page tell other tabs about its own edits.
//   • puVersion: increments once per accepted Public Utilities event — pass it to anything that
//     should reload (the PU map layer, the due board) without subscribing again.
//   • lastEventAt + secondsSinceEvent() for the "Live" badge.
// --- END UPDATE
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from 'react';
import webSocketService from '../services/websocketService';
// --- UPDATE
import { subscribe as subscribeBus, publish as publishBus, BUS_EVENTS } from '../utils/flowsenseBus'; // --- UPDATE (v3): + publish
// --- END UPDATE

export const LIVE_REFRESH_COALESCE_MS = 3000;
export const MAX_RECENT_EVENTS = 15;
// --- UPDATE: same entity + kind inside this window → one feed entry (bus + server echo)
export const FEED_DEDUPE_MS = 4000;
// --- END UPDATE

// --- UPDATE: work_order_events types that can change what this page shows
const WO_EVENT_REFRESH_TYPES = new Set(['created', 'updated', 'assigned', 'status_updated', 'deleted']);
// --- END UPDATE

/** event → how the page reacts. kind: utility | asset | sensor | work | ai */
export const PUBLIC_UTILITIES_EVENTS = Object.freeze({
    utility_status_update: { kind: 'utility', label: 'Utility status changed', refresh: true },
    utility_updated: { kind: 'utility', label: 'Utility updated', refresh: true },
    asset_updated: { kind: 'asset', label: 'Asset updated', refresh: true },
    asset_update: { kind: 'asset', label: 'Asset updated', refresh: true },
    asset_created: { kind: 'asset', label: 'Asset added', refresh: true },
    asset_deleted: { kind: 'asset', label: 'Asset removed', refresh: true },
    asset_status_changed: { kind: 'asset', label: 'Asset status changed', refresh: false },
    anomaly_recorded: { kind: 'sensor', label: 'Sensor anomaly', refresh: false },
    sensor_status_update: { kind: 'sensor', label: 'Sensor status changed', refresh: true },
    new_water_level_reading: { kind: 'sensor', label: 'Water level alarm', refresh: false, onlyIf: (d) => d && (d.level === 'HIGH' || d.level === 'EMPTY') },
    work_order_created: { kind: 'work', label: 'Work order created', refresh: true },
    work_order_completed: { kind: 'work', label: 'Work order completed', refresh: true },
    // --- UPDATE: full work-order lifecycle
    work_order_updated: { kind: 'work', label: 'Work order updated', refresh: true },
    work_order_assigned: { kind: 'work', label: 'Work order assigned', refresh: false },
    work_order_deleted: { kind: 'work', label: 'Work order removed', refresh: true },
    // The backend controller broadcasts ONE channel with a `type` field:
    //   work_order_events { type: created | updated | assigned | status_updated | deleted | … , workOrderId }
    work_order_events: { kind: 'work', label: 'Work order changed', refresh: true, onlyIf: (d) => WO_EVENT_REFRESH_TYPES.has(d?.type) },
    road_update: { kind: 'work', label: 'Road / asset maintenance logged', refresh: true },
    // --- END UPDATE
    issue_reported: { kind: 'work', label: 'Issue reported', refresh: true },
    maintenance_logged: { kind: 'work', label: 'Maintenance logged', refresh: true },
    asset_prediction_update: { kind: 'ai', label: 'AI failure prediction', refresh: false },
    ai_flood_prediction: { kind: 'ai', label: 'AI flood prediction', refresh: false, onlyIf: (d) => ['HIGH', 'CRITICAL'].includes(String(d?.predictedSeverity || d?.flood_risk || '').toUpperCase()) },
    critical_anomaly_detected: { kind: 'ai', label: 'Critical twin anomaly', refresh: false },
    // --- UPDATE (v3): Public Utilities module events (see services/puEvents.js on the backend)
    asset_lifecycle_update: { kind: 'asset', label: 'Asset record updated', refresh: true, pu: true, labelOf: (d) => LIFECYCLE_LABELS[d?.kind] },
    assets_imported: { kind: 'asset', label: 'Assets imported', refresh: true, pu: true, labelOf: (d) => (d?.rolled_back != null ? `Import undone (${d.rolled_back} assets)` : d?.inserted != null ? `${d.inserted} assets imported` : null) },
    public_utilities_update: { kind: 'asset', label: 'Public Utilities updated', refresh: true, pu: true, labelOf: (d) => REGISTRY_LABELS[d?.scope] },
    // --- END UPDATE
});

// --- UPDATE (v3)
const LIFECYCLE_LABELS = Object.freeze({
    maintenance: 'Maintenance logged', inspection: 'Inspection submitted', schedule: 'Inspection scheduled',
    schedule_bulk: 'Inspections scheduled', status: 'Asset status changed', photo: 'Photo added', manager: 'Managed-by changed',
});
const REGISTRY_LABELS = Object.freeze({
    managers: 'Asset managers changed', assignment: 'Assets assigned to a manager', zones: 'Zones changed',
    codes: 'Constituency code confirmed', rezone: 'Assets placed in constituencies', import: 'Assets imported',
});
const ASSET_BUS = (BUS_EVENTS && BUS_EVENTS.ASSET_CHANGED) || 'asset_changed';
// --- END UPDATE

const isDocHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
const assetIdOf = (d) => d?.asset_id ?? d?.assetId ?? d?.asset?.id ?? d?.data?.asset_id ?? d?.data?.id ?? null;
const unwrap = (d) => (d && typeof d === 'object' && d.payload && typeof d.payload === 'object' ? d.payload // --- UPDATE (v3): { type, payload } envelope
    : d && typeof d === 'object' && d.data && typeof d.data === 'object' ? d.data : d);
// --- UPDATE
const workOrderIdOf = (d) => d?.work_order_id ?? d?.workOrderId ?? d?.id ?? null;
// --- END UPDATE

/**
 * @param {object}   opts
 * @param {boolean}  [opts.enabled=true]
 * @param {(reason:string)=>void} opts.onRefresh            full reload (coalesced)
 * @param {(patch:{id,…})=>void}   [opts.onUtilityPatch]    in-place utility update
 * @param {(patch:{id,…})=>void}   [opts.onAssetPatch]      in-place asset update
 * @param {(evt:object)=>void}     [opts.onEvent]           every accepted event (toasts etc.)
 */
export default function usePublicUtilitiesLive({ enabled = true, onRefresh, onUtilityPatch, onAssetPatch, onEvent, coalesceMs = LIVE_REFRESH_COALESCE_MS } = {}) {
    const [isConnected, setIsConnected] = useState(() => {
        try { return !!webSocketService.isConnected?.(); } catch { return false; }
    });
    const [recentEvents, setRecentEvents] = useState([]);
    const [eventCount, setEventCount] = useState(0);
    const [pendingRefresh, setPendingRefresh] = useState(false);
    // --- UPDATE (v3)
    const [puVersion, setPuVersion] = useState(0);
    const [lastEventAt, setLastEventAt] = useState(null);
    // --- END UPDATE

    const cb = useRef({});
    cb.current = { onRefresh, onUtilityPatch, onAssetPatch, onEvent };
    const timerRef = useRef(null);
    const reasonsRef = useRef(new Set());
    const hiddenPendingRef = useRef(false);
    const mountedRef = useRef(true);
    // --- UPDATE
    const seenRef = useRef(new Map()); // dedupe key → timestamp
    // --- END UPDATE

    const flush = useCallback(() => {
        timerRef.current = null;
        if (!mountedRef.current) return;
        const reasons = [...reasonsRef.current];
        reasonsRef.current.clear();
        setPendingRefresh(false);
        cb.current.onRefresh?.(reasons.join(', ') || 'live');
    }, []);

    /** Coalesce: the first event starts the timer, the rest ride along. */
    const scheduleRefresh = useCallback((reason = 'live') => {
        reasonsRef.current.add(reason);
        if (isDocHidden()) { hiddenPendingRef.current = true; return; }
        setPendingRefresh(true);
        if (timerRef.current) return;
        timerRef.current = setTimeout(flush, coalesceMs);
    }, [flush, coalesceMs]);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; clearTimeout(timerRef.current); };
    }, []);

    // Catch up when the tab becomes visible again
    useEffect(() => {
        const onVis = () => {
            if (!isDocHidden() && hiddenPendingRef.current) {
                hiddenPendingRef.current = false;
                scheduleRefresh('tab visible');
            }
        };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [scheduleRefresh]);

    // Subscribe ONCE
    useEffect(() => {
        if (!enabled) return undefined;
        try { webSocketService.connect?.(); } catch { /* service handles its own retries */ }

        // --- UPDATE: dedupe — returns true if this entity/kind was recorded moments ago
        const isDuplicate = (key) => {
            if (!key) return false;
            const now = Date.now();
            const seen = seenRef.current;
            for (const [k, t] of seen) if (now - t > FEED_DEDUPE_MS) seen.delete(k);
            if (seen.has(key)) return true;
            seen.set(key, now);
            return false;
        };
        // --- END UPDATE

        const record = (name, cfg, data, dedupeKey) => {
            if (!mountedRef.current) return null; // --- UPDATE
            if (isDuplicate(dedupeKey)) return null; // --- UPDATE
            const payload = unwrap(data);
            const evt = {
                id: `${name}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                name, kind: cfg.kind, label: (cfg.labelOf && cfg.labelOf(payload || {})) || cfg.label, // --- UPDATE (v3): specific label
                pu: !!cfg.pu, puKind: payload?.kind || payload?.scope || null, previousStatus: payload?.previousStatus ?? payload?.previous_status ?? null, // --- UPDATE (v3)
                assetId: assetIdOf(data) ?? assetIdOf(payload),
                utilityId: payload?.utility_id ?? payload?.utilityId ?? (cfg.kind === 'utility' ? payload?.id : undefined),
                status: payload?.status ?? payload?.newStatus,
                title: payload?.name || payload?.title || payload?.assetName || payload?.wellName || null,
                at: new Date().toISOString(),
                data,
            };
            if (mountedRef.current) setIsConnected(true); // a message arrived → the socket is up
            setRecentEvents(prev => [evt, ...prev].slice(0, MAX_RECENT_EVENTS));
            setEventCount(n => n + 1);
            setLastEventAt(Date.now()); // --- UPDATE (v3)
            if (cfg.pu || cfg.kind === 'asset') setPuVersion(v => v + 1); // --- UPDATE (v3)
            cb.current.onEvent?.(evt);
            return evt;
        };

        const handlers = Object.entries(PUBLIC_UTILITIES_EVENTS).map(([name, cfg]) => {
            const handler = (data) => {
                if (cfg.onlyIf && !cfg.onlyIf(data)) return;
                const payload = unwrap(data) || {};
                // --- UPDATE: work-order events share a key with the bus so the feed shows one entry
                const woId = cfg.kind === 'work' ? workOrderIdOf(payload) : null;
                // --- UPDATE (v3): the same asset change from two channels (lifecycle + status) → one feed entry
                const aId = cfg.kind === 'asset' ? (assetIdOf(payload) ?? assetIdOf(data)) : null;
                const assetKey = aId != null && (name === 'asset_status_changed' || (name === 'asset_lifecycle_update' && payload.kind === 'status')) ? `asset-status:${aId}:${payload.status ?? payload.newStatus ?? ''}` : null;
                // --- END UPDATE
                const evt = record(name, cfg, data, woId != null ? `wo:${woId}` : assetKey)
                    || { assetId: assetIdOf(data) ?? assetIdOf(payload), utilityId: payload?.utility_id ?? payload?.utilityId ?? (cfg.kind === 'utility' ? payload?.id : undefined), status: payload?.status ?? payload?.newStatus };
                // --- END UPDATE

                // In-place patches — UI updates before the reload
                if (cfg.kind === 'utility' && evt.utilityId != null && payload.status) {
                    cb.current.onUtilityPatch?.({ id: evt.utilityId, status: payload.status, ...(payload.description ? { description: payload.description } : {}) });
                }
                if (name === 'asset_status_changed' && evt.assetId != null && evt.status) {
                    cb.current.onAssetPatch?.({ id: evt.assetId, status: evt.status });
                }
                // --- UPDATE (v3): Public Utilities status change → patch in place before the reload
                if (name === 'asset_lifecycle_update' && payload.kind === 'status' && evt.assetId != null && payload.status) {
                    cb.current.onAssetPatch?.({ id: evt.assetId, status: payload.status });
                }
                // --- END UPDATE
                if (name === 'anomaly_recorded' && evt.assetId != null) {
                    cb.current.onAssetPatch?.({ id: evt.assetId, __incrementAnomalies: true });
                }
                if (cfg.refresh) scheduleRefresh(cfg.label);
            };
            webSocketService.subscribe(name, handler);
            return [name, handler];
        });

        // Connection state — support both the event-bus and the 'status' channel styles
        const onConnect = () => mountedRef.current && setIsConnected(true);
        const onDisconnect = () => mountedRef.current && setIsConnected(false);
        const onStatus = (e) => {
            const s = e?.status || e;
            if (s === 'connected') onConnect();
            else if (s === 'disconnected' || s === 'error') onDisconnect();
        };
        webSocketService.on?.('connect', onConnect);
        webSocketService.on?.('disconnect', onDisconnect);
        webSocketService.subscribe('status', onStatus);

        // --- UPDATE: cross-page bus — work orders / roads changed on another page or tab
        const offBus = subscribeBus((type, payload = {}) => {
            // --- UPDATE (v3): asset edits in another tab (or another page in this tab)
            if (type === ASSET_BUS) {
                if (payload.origin === 'public_utilities_live') return; // our own announcement
                record(type, { kind: 'asset', label: `Asset ${payload.action || 'changed'}`, pu: true }, payload, payload.id != null ? `asset:${payload.id}:${payload.action || ''}` : null);
                if (payload.id != null && payload.status) cb.current.onAssetPatch?.({ id: payload.id, status: payload.status });
                scheduleRefresh('Asset changed');
                return;
            }
            // --- END UPDATE
            if (type !== BUS_EVENTS.WORK_ORDER_CHANGED && type !== BUS_EVENTS.ROAD_CHANGED) return;
            const isRoad = type === BUS_EVENTS.ROAD_CHANGED;
            const cfg = { kind: 'work', label: isRoad ? `Road ${payload.action || 'changed'}` : `Work order ${payload.action || 'changed'}` };
            const key = isRoad ? (payload.id != null ? `road:${payload.id}` : null) : (payload.id != null ? `wo:${payload.id}` : null);
            record(type, cfg, payload, key);
            if (payload.asset_id != null && payload.status) {
                cb.current.onAssetPatch?.({ id: payload.asset_id, last_work_order_id: payload.id ?? null, last_work_order_status: payload.status });
            }
            scheduleRefresh(cfg.label);
        });
        // --- END UPDATE

        return () => {
            offBus(); // --- UPDATE
            handlers.forEach(([name, h]) => webSocketService.unsubscribe(name, h));
            webSocketService.off?.('connect', onConnect);
            webSocketService.off?.('disconnect', onDisconnect);
            webSocketService.unsubscribe('status', onStatus);
        };
    }, [enabled, scheduleRefresh]);

    const clearEvents = useCallback(() => setRecentEvents([]), []);
    // --- UPDATE (v3): tell other tabs / pages that THIS page changed an asset (server events reach everyone
    //     else; this covers tabs whose socket is reconnecting and pages that only listen to the bus)
    const announceAssetChange = useCallback((payload = {}) => {
        try { publishBus(ASSET_BUS, { ...payload, origin: 'public_utilities_live' }); } catch { /* bus optional */ }
    }, []);
    const secondsSinceEvent = useCallback(() => (lastEventAt ? Math.round((Date.now() - lastEventAt) / 1000) : null), [lastEventAt]);
    // --- END UPDATE

    return {
        isConnected, recentEvents, eventCount, lastEvent: recentEvents[0] || null, pendingRefresh, scheduleRefresh, clearEvents,
        puVersion, lastEventAt, announceAssetChange, secondsSinceEvent, // --- UPDATE (v3)
    };
}